import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import type { User, UserPermissions } from './types';
import { authApi, roleApi } from './api';

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  isFinance: boolean;
  permissions: UserPermissions;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  refreshUser: () => Promise<void>;
  checkPermission: (page: string, action: 'view' | 'create' | 'edit' | 'delete') => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions>({});
  const [loading, setLoading] = useState(true);

  const isAdmin = useMemo(() => {
  const result = user?.role?.toLowerCase() === 'admin' || user?.role === '管理员';
  console.log('[Auth] 用户信息:', { user: user?.name, role: user?.role, isAdmin: result });
  return result;
}, [user?.role]);

const isFinance = useMemo(() => {
  const result = user?.role?.toLowerCase() === 'finance' || user?.role === '财务';
  console.log('[Auth] isFinance:', result);
  return result;
}, [user?.role]);

  /** 默认权限（包含所有页面的基础权限） */
  const defaultPerms: UserPermissions = {
    dashboard: { can_view: true, can_create: false, can_edit: false, can_delete: false },
    report: { can_view: true, can_create: false, can_edit: false, can_delete: false }, // 结算报表
    brokers: { can_view: false, can_create: false, can_edit: false, can_delete: false },
    formulas: { can_view: false, can_create: false, can_edit: false, can_delete: false },
    import: { can_view: false, can_create: false, can_edit: false, can_delete: false },
    'report-template': { can_view: false, can_create: false, can_edit: false, can_delete: false },
    users: { can_view: false, can_create: false, can_edit: false, can_delete: false },
    roles: { can_view: false, can_create: false, can_edit: false, can_delete: false },
    bills: { can_view: false, can_create: false, can_edit: false, can_delete: false },
    'wage-declaration': { can_view: false, can_create: false, can_edit: false, can_delete: false },
  };

  /** 根据用户角色加载权限配置 */
  const loadRolePermissions = useCallback(async (roleName: string) => {
    try {
      // admin 直接给全部权限
      if (roleName.toLowerCase() === 'admin' || roleName === '管理员') {
        setPermissions({
          dashboard: { can_view: true, can_create: true, can_edit: true, can_delete: true },
          brokers: { can_view: true, can_create: true, can_edit: true, can_delete: true },
          formulas: { can_view: true, can_create: true, can_edit: true, can_delete: true },
          import: { can_view: true, can_create: true, can_edit: true, can_delete: true },
          report: { can_view: true, can_create: true, can_edit: true, can_delete: true },
          'report-template': { can_view: true, can_create: true, can_edit: true, can_delete: true },
          users: { can_view: true, can_create: true, can_edit: true, can_delete: true },
          roles: { can_view: true, can_create: true, can_edit: true, can_delete: true },
          bills: { can_view: true, can_create: true, can_edit: true, can_delete: true },
          'wage-declaration': { can_view: true, can_create: true, can_edit: true, can_delete: true },
        });
        return;
      }
      // 其他角色从 roles 表读取权限
      const perms = await authApi.rolePermissions(roleName);
      // 将权限配置转换为对象格式（兼容数组和对象两种格式）
      const permsObj: UserPermissions = {};
      if (perms && Array.isArray(perms)) {
        // 数组格式
        for (const p of perms) {
          if (p.page) {
            permsObj[p.page] = {
              can_view: !!p.can_view,
              can_create: !!p.can_create,
              can_edit: !!p.can_edit,
              can_delete: !!p.can_delete,
            };
          }
        }
      } else if (perms && typeof perms === 'object') {
        // 对象格式
        for (const [page, perm] of Object.entries(perms)) {
          permsObj[page] = {
            can_view: !!perm.can_view,
            can_create: !!perm.can_create,
            can_edit: !!perm.can_edit,
            can_delete: !!perm.can_delete,
          };
        }
      }
      const merged = { ...defaultPerms, ...permsObj };
      setPermissions(merged);
      console.log('[Auth] 加载角色权限:', roleName, 'pages:', Object.keys(merged).join(', '), 'count:', Object.keys(merged).length);
    } catch (e: any) {
      console.error('[Auth] 加载角色权限失败:', roleName, e.message);
      setPermissions(defaultPerms);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUser(me);
      if (me && me.role) {
        await loadRolePermissions(me.role);
      }
    } catch {
      setUser(null);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [loadRolePermissions]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (username: string, password: string) => {
    const res = await authApi.login(username, password);
    if (res.token) {
      localStorage.setItem("token", res.token);
    }
    setUser(res.user);
    if (res.user && res.user.role) {
      await loadRolePermissions(res.user.role);
    }
  };

  const logout = () => {
    authApi.logout();
    localStorage.removeItem("token");
    setUser(null);
    setPermissions({});
    window.location.href = '/login';
  };

  const checkPermission = (page: string, action: 'view' | 'create' | 'edit' | 'delete'): boolean => {
    if (isAdmin) return true;
    const perm = permissions[page];
    if (!perm) return false;
    return perm[`can_${action}`] === true;
  };

  return (
    <AuthContext.Provider value={{ user, isAdmin, isFinance, permissions, login, logout, loading, refreshUser, checkPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
